import {
  type FuelAnalysis,
  fuelAnalysis,
  type PerformanceReference,
  ROB_TOLERANCE_MT,
  type RobFlowRow,
  type RobIssue,
  VOYAGE_STATES,
} from '@felix/domain';
import { Button, Checkbox, SegmentedControl, Select, Skeleton } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendarStats,
  IconGasStation,
  IconListDetails,
  IconRoute,
  IconScale,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import classes from './FuelAnalysisScreen.module.css';
import {
  defaultFuelPeriod,
  FUEL_SCOPES,
  type FuelScope,
  type FuelScopeInput,
  fuelScopeReports,
} from './fuel-analysis.js';
import { useNumber } from './numbers.js';
import { formatDuration } from './overview.js';
import { voyageReference } from './reference.js';
import { useReportLabels } from './report-labels.js';
import type { VoyageSource } from './source.js';
import type { CharterPartyRegisterData, Voyage, VoyageReport } from './types.js';
import { pickVoyage } from './VoyageFrame.js';
import record from './VoyageRecordScreens.module.css';
import { useReferenceLabel } from './VoyageReference.js';

const ISSUE_TONE: Record<RobIssue, StatusTone> = {
  rob_higher: 'warning',
  rob_lower: 'danger',
  consumption_missing: 'info',
};
const two = { digits: 2, fixed: true } as const;
const one = { digits: 1, fixed: true } as const;

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      vessels: PersonnelVessel[];
      voyages: Voyage[];
      reports: VoyageReport[];
      /** Seferin referansı için register (ADR 0084 Ek 2); eski kaynakta yok. */
      register: CharterPartyRegisterData | null;
    };

export interface FuelAnalysisScreenProps {
  source: VoyageSource;
  /** Saat (testlerde sabit). */
  now?: () => Date;
}

/** Geminin bütün raporları: All Reports ucu; eski kaynakta seferlerin raporları birleşir. */
async function vesselReports(
  source: VoyageSource,
  vesselId: string,
  voyages: readonly Voyage[],
): Promise<VoyageReport[]> {
  if (source.allReports) return source.allReports(vesselId);
  const lists = await Promise.all(voyages.map((v) => source.reports(v.id)));
  const seen = new Map<string, VoyageReport>();
  for (const report of lists.flat()) seen.set(report.id, report);
  return [...seen.values()];
}

/**
 * Voyage ▸ Performance & Analysis ▸ Fuel & Consumption Analysis (aşama V6; README S21; ADR 0090):
 * sefer ya da tarih aralığı için yakıt türüne ve duruma göre tüketim, günlük ortalama, mil başına
 * tüketim, bacak ve dönem dökümü, ikmal mutabakatı ve ROB akışı. Raporlardan türetilir (S12);
 * gemide ve ofiste aynı ekran (ADR 0019 §2), ikisinde de salt okunur.
 */
export function FuelAnalysisScreen({ source, now = () => new Date() }: FuelAnalysisScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.fuelAnalysis.${key}`, options);
  const params = new URLSearchParams(shell?.search ?? '');
  const [vesselId, setVesselId] = useState<string | null>(() => params.get('vessel'));
  const [voyageId, setVoyageId] = useState<string | null>(() => params.get('voyage'));
  const [scope, setScope] = useState<FuelScope>(() =>
    params.get('scope') === 'period' ? 'period' : 'voyage',
  );
  const [period, setPeriod] = useState<{ from: string | null; to: string | null } | null>(null);
  const [state, setState] = useState<Load>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const vessels = await source.vessels();
      const vessel = vessels.find((v) => v.id === vesselId) ?? vessels[0];
      const voyages = vessel ? await source.voyages(vessel.id) : [];
      const reports = vessel ? await vesselReports(source, vessel.id, voyages) : [];
      const register =
        vessel && source.charterParties
          ? await source.charterParties(vessel.id).catch(() => null)
          : null;
      setState({ kind: 'ready', vessels, voyages, reports, register });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  const vessel = ready?.vessels.find((v) => v.id === vesselId) ?? ready?.vessels[0] ?? null;
  const voyages = useMemo(
    () => (ready?.voyages ?? []).filter((v) => v.status !== 'cancelled'),
    [ready],
  );
  const voyage = pickVoyage(voyages, voyageId);
  const fallback = useMemo(() => defaultFuelPeriod(ready?.reports ?? [], now()), [ready, now]);
  const from = period?.from ?? fallback.from;
  const to = period?.to ?? fallback.to;

  const scoped = scope === 'voyage' ? voyage : null;
  const computed = useMemo(() => {
    if (!ready) return null;
    const input: FuelScopeInput | null =
      scope === 'period'
        ? { kind: 'period', from, to }
        : scoped
          ? { kind: 'voyage', voyage: scoped }
          : null;
    if (!input) return null;
    const own = fuelScopeReports(ready.reports, input);
    return { analysis: fuelAnalysis(own.reports, { end: own.end }), count: own.reports.length };
  }, [ready, scope, scoped, from, to]);
  const analysis = computed?.analysis ?? null;
  const count = computed?.count ?? 0;

  const header = <PageHeader title={k('title')} description={k('description')} />;

  if (state.kind === 'error') {
    return (
      <div className={classes.page}>
        {header}
        <Panel>
          <EmptyState
            tone="danger"
            title={k('error')}
            description={t(`voyage.errors.${state.code}`, {
              defaultValue: t('voyage.errors.GENERIC'),
            })}
            action={
              <Button variant="light" onClick={() => void load()}>
                {t('common.retry')}
              </Button>
            }
          />
        </Panel>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className={classes.page} aria-busy="true">
        {header}
        <Skeleton height={88} radius="md" />
        <Skeleton height={160} radius="md" />
        <Skeleton height={260} radius="md" />
      </div>
    );
  }

  return (
    <div className={classes.page}>
      {header}
      <Panel>
        <div className={classes.toolbar}>
          {source.scope === 'web' && ready.vessels.length > 1 ? (
            <Select
              label={k('vessel')}
              data={ready.vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={vessel?.id ?? null}
              allowDeselect={false}
              onChange={(value) => {
                if (!value) return;
                setVesselId(value);
                setVoyageId(null);
                setPeriod(null);
              }}
              className={classes.field}
            />
          ) : null}
          <div className={classes.scope}>
            <span className={classes.label} id="fuel-scope-label">
              {k('scope')}
            </span>
            <SegmentedControl
              aria-labelledby="fuel-scope-label"
              value={scope}
              onChange={(value) => setScope(value as FuelScope)}
              data={FUEL_SCOPES.map((value) => ({ value, label: k(`scopes.${value}`) }))}
            />
          </div>
          {scope === 'voyage' ? (
            <Select
              label={k('voyage')}
              data={voyages.map((v) => ({
                value: v.id,
                label: `${v.voyageNo} · ${t(`voyage.voyageStatus.${v.status}`)}`,
              }))}
              value={voyage?.id ?? null}
              placeholder={k('noVoyages')}
              disabled={voyages.length === 0}
              allowDeselect={false}
              onChange={(value) => value && setVoyageId(value)}
              className={classes.field}
            />
          ) : (
            <>
              <DayInput
                label={k('from')}
                value={from}
                onChange={(value) => setPeriod({ from: value, to })}
                className={classes.day}
              />
              <DayInput
                label={k('to')}
                value={to}
                onChange={(value) => setPeriod({ from, to: value })}
                className={classes.day}
              />
            </>
          )}
        </div>
        {analysis?.summary.start ? (
          <p className={classes.covers}>
            {k('covers', {
              from: formatDateTime(analysis.summary.start, 0),
              to: formatDateTime(analysis.summary.end, 0),
              count,
            })}
          </p>
        ) : null}
      </Panel>
      {analysis?.summary.start && analysis.summary.reportCount > 1 ? (
        <FuelAnalysisView
          analysis={analysis}
          scope={scope}
          // Sefer kapsamında referans: CP > Owner's reference > yok (ADR 0084 Ek 2)
          reference={
            scope === 'voyage' && voyage ? voyageReference(voyage, ready.register).reference : null
          }
        />
      ) : (
        <Panel>
          <EmptyState
            icon={<IconGasStation size={22} stroke={1.8} />}
            title={scope === 'voyage' && !voyage ? k('noVoyages') : k('noDataTitle')}
            description={k('noData')}
          />
        </Panel>
      )}
    </div>
  );
}

/** Kaydırılabilir tablo kutusu (telefonda tablo kendi kutusunda kayar; sayfa yana kaymaz). */
function TableBox({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section
      className={`${table.tableWrap} ${record.tableBox}`}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      {children}
    </section>
  );
}

/** Analizin gövdesi (veri dışarıdan; test edilebilir). */
export function FuelAnalysisView({
  analysis,
  scope,
  reference = null,
}: {
  analysis: FuelAnalysis;
  scope: FuelScope;
  /** Seferin referansı (ADR 0084 Ek 2); tarih aralığında yok. */
  reference?: PerformanceReference | null;
}) {
  return (
    <div className={classes.stack}>
      <FiguresPanel analysis={analysis} scope={scope} reference={reference} />
      <ByFuelPanel analysis={analysis} />
      <ReconciliationPanel analysis={analysis} />
      <div className={classes.columns}>
        <LegsPanel analysis={analysis} />
        <PeriodsPanel analysis={analysis} />
      </div>
      <RobFlowPanel analysis={analysis} />
    </div>
  );
}

function FiguresPanel({
  analysis,
  scope,
  reference,
}: {
  analysis: FuelAnalysis;
  scope: FuelScope;
  reference: PerformanceReference | null;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const referenceLabel = useReferenceLabel();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.fuelAnalysis.${key}`, options);
  const sea = analysis.fuels.reduce((sum, f) => sum + f.byState.sea + f.byState.waiting, 0);
  const seaHours = analysis.fuelHours.sea + analysis.fuelHours.waiting;
  const seaDaily = analysis.totalMt === null || seaHours <= 0 ? null : (sea / seaHours) * 24;
  const tiles: {
    key: string;
    label: string;
    value: string;
    unit?: string;
    sub?: string;
    tone?: string;
  }[] = [
    { key: 'total', label: k('tiles.total'), value: n(analysis.totalMt, one), unit: 'MT' },
    { key: 'daily', label: k('tiles.daily'), value: n(analysis.dailyMt, one), unit: 'MT/day' },
    { key: 'seaDaily', label: k('tiles.seaDaily'), value: n(seaDaily, one), unit: 'MT/day' },
    {
      key: 'perNm',
      label: k('tiles.perNm'),
      value: n(analysis.seaKgPerNm, one),
      unit: 'kg/nm',
      sub: k('tiles.perNmSub'),
    },
    {
      key: 'distance',
      label: k('tiles.distance'),
      value: n(analysis.summary.distanceNm, 0),
      unit: 'nm',
    },
    {
      key: 'hours',
      label: k('tiles.hours'),
      value: formatDuration(analysis.fuelHours.total),
    },
    {
      key: 'warnings',
      label: k('tiles.warnings'),
      value: n(analysis.issues, 0),
      sub: analysis.issues > 0 ? k('tiles.warningsSome') : k('tiles.warningsNone'),
      tone: analysis.issues > 0 ? 'warning' : 'success',
    },
  ];
  // Referans tüketimi (ADR 0084 Ek 2): "Daily at Sea" kutusunun altında, ayrı kutu değil (kutular
  // tek satırda kalır); CP no ya da "Owner's reference", sonuç Indicative
  const seaTile = tiles.find((tile) => tile.key === 'seaDaily');
  if (reference && seaTile && reference.kind !== 'none') {
    seaTile.sub = t('voyage.cpReference.fuelSub', {
      laden: n(reference.consumptionLadenMt, one),
      ballast: n(reference.consumptionBallastMt, one),
      label:
        reference.kind === 'charter_party'
          ? (reference.cpNo ?? referenceLabel(reference))
          : referenceLabel(reference),
    });
  }
  const open = analysis.summary.hours.total > analysis.fuelHours.total;
  return (
    <Panel title={k('figures')} icon={<IconCalendarStats size={20} stroke={1.8} />}>
      {scope === 'period' || open ? (
        <p className={record.note}>
          {[scope === 'period' ? k('periodNote') : null, open ? k('openNote') : null]
            .filter(Boolean)
            .join(' ')}
        </p>
      ) : null}
      <ul className={classes.tiles}>
        {tiles.map((tile) => (
          <li key={tile.key} className={classes.tile} data-tone={tile.tone}>
            <span className={classes.tileLabel}>{tile.label}</span>
            <strong className={classes.tileValue}>
              {tile.value}
              {tile.unit && tile.value !== '—' ? (
                <span className={classes.tileUnit}> {tile.unit}</span>
              ) : null}
            </strong>
            {tile.sub ? <span className={classes.tileLabel}>{tile.sub}</span> : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function ByFuelPanel({ analysis }: { analysis: FuelAnalysis }) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.fuelAnalysis.${key}`, options);
  const c = (key: string) => k(`columns.${key}`);
  const title = k('byFuel');
  const dailyTitle = k('dailyTitle');
  const max = Math.max(0, ...analysis.fuels.map((f) => f.totalMt));
  const passage = analysis.fuels.reduce((sum, f) => sum + f.passageMt, 0);
  return (
    <Panel title={title} icon={<IconGasStation size={20} stroke={1.8} />} flush>
      <TableBox label={title}>
        <table
          className={`${table.table} ${record.table} ${classes.wrapHead} ${classes.fuelTable}`}
        >
          <thead>
            <tr>
              <th scope="col">{c('fuel')}</th>
              {VOYAGE_STATES.map((state) => (
                <th key={state} scope="col" className={record.num}>
                  {k(`states.${state}`)}
                </th>
              ))}
              <th scope="col" className={record.num}>
                {c('total')}
              </th>
              <th scope="col" className={record.num}>
                {c('daily')}
              </th>
              <th scope="col" className={record.num}>
                {c('perNm')}
              </th>
            </tr>
          </thead>
          <tbody>
            {analysis.fuels.map((row) => (
              <tr key={row.fuel}>
                <th scope="row">{row.fuel}</th>
                {VOYAGE_STATES.map((state) => (
                  <td key={state} className={record.num}>
                    {n(row.byState[state], two)}
                  </td>
                ))}
                <td className={`${record.num} ${record.strong}`}>{n(row.totalMt, two)}</td>
                <td className={record.num}>{n(row.dailyMt, two)}</td>
                <td className={record.num}>{n(row.seaKgPerNm, one)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableBox>
      <div className={classes.chart}>
        <span className={record.timeTitle}>{k('chart')}</span>
        <ul className={classes.bars}>
          {analysis.fuels.map((row) => (
            <li key={row.fuel} className={classes.barRow}>
              <span className={classes.barName}>{row.fuel}</span>
              <span className={classes.barTrack}>
                <span
                  className={classes.barFill}
                  role="img"
                  aria-label={k('chartFuel', { fuel: row.fuel, total: n(row.totalMt, two) })}
                  style={{ width: max > 0 ? `${(row.totalMt / max) * 100}%` : '0%' }}
                >
                  {VOYAGE_STATES.filter((state) => row.byState[state] > 0).map((state) => (
                    <span
                      key={state}
                      className={record.barPart}
                      data-state={state}
                      style={{ width: `${(row.byState[state] / row.totalMt) * 100}%` }}
                      title={`${row.fuel} · ${k(`states.${state}`)} · ${n(row.byState[state], two)} MT`}
                    />
                  ))}
                </span>
              </span>
              <span className={classes.barValue}>{n.unit(row.totalMt, 'MT', one)}</span>
            </li>
          ))}
        </ul>
        <ul className={record.barLegend}>
          {VOYAGE_STATES.map((state) => (
            <li key={state}>
              <span className={record.legendDot} data-state={state} aria-hidden />
              {k(`states.${state}`)}
            </li>
          ))}
        </ul>
        {passage > 0 ? (
          <p className={record.note}>{k('passage', { value: n(passage, two) })}</p>
        ) : null}
      </div>
      <h3 className={classes.subTitle}>{dailyTitle}</h3>
      <TableBox label={dailyTitle}>
        <table className={`${table.table} ${record.table} ${classes.wrapHead}`}>
          <thead>
            <tr>
              <th scope="col">{c('fuel')}</th>
              {VOYAGE_STATES.map((state) => (
                <th key={state} scope="col" className={record.num}>
                  {k(`states.${state}`)}
                </th>
              ))}
              <th scope="col" className={record.num}>
                {c('overall')}
              </th>
            </tr>
          </thead>
          <tbody>
            {analysis.fuels.map((row) => (
              <tr key={row.fuel}>
                <th scope="row">{row.fuel}</th>
                {VOYAGE_STATES.map((state) => (
                  <td key={state} className={record.num}>
                    {n(row.dailyByState[state], two)}
                  </td>
                ))}
                <td className={`${record.num} ${record.strong}`}>{n(row.dailyMt, two)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableBox>
    </Panel>
  );
}

function IssueBadge({ issue, opening }: { issue: RobIssue | null; opening?: boolean }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.fuelAnalysis.${key}`);
  if (opening) return <StatusBadge tone="neutral">{k('status.opening')}</StatusBadge>;
  return issue ? (
    <span title={k(`hints.${issue}`)}>
      <StatusBadge tone={ISSUE_TONE[issue]} withIcon>
        {k(`status.${issue}`)}
      </StatusBadge>
    </span>
  ) : (
    <StatusBadge tone="success">{k('status.ok')}</StatusBadge>
  );
}

function ReconciliationPanel({ analysis }: { analysis: FuelAnalysis }) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.fuelAnalysis.${key}`, options);
  const c = (key: string) => k(`columns.${key}`);
  const title = k('reconciliation');
  return (
    <Panel title={title} icon={<IconScale size={20} stroke={1.8} />} flush>
      <p className={`${record.note} ${classes.pad}`}>
        {k('reconNote', { tolerance: n(ROB_TOLERANCE_MT, 1) })}
      </p>
      <TableBox label={title}>
        <table className={`${table.table} ${record.table} ${classes.wrapHead}`}>
          <thead>
            <tr>
              <th scope="col">{c('fuel')}</th>
              <th scope="col" className={record.num}>
                {c('opening')}
              </th>
              <th scope="col" className={record.num}>
                {c('bunkered')}
              </th>
              <th scope="col" className={record.num}>
                {c('closing')}
              </th>
              <th scope="col" className={record.num}>
                {c('robConsumption')}
              </th>
              <th scope="col" className={record.num}>
                {c('reported')}
              </th>
              <th scope="col" className={record.num}>
                {c('difference')}
              </th>
              <th scope="col" className={classes.colStatus}>
                {c('status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {analysis.fuels.map(({ fuel, reconciliation: r }) => (
              <tr key={fuel}>
                <th scope="row">{fuel}</th>
                <td className={record.num}>{n(r.openingMt, two)}</td>
                <td className={record.num}>{n(r.bunkeredMt, two)}</td>
                <td className={record.num}>{n(r.closingMt, two)}</td>
                <td className={record.num}>{n(r.robConsumptionMt, two)}</td>
                <td className={record.num}>{n(r.reportedMt, two)}</td>
                <td className={`${record.num} ${record.strong}`}>{n(r.differenceMt, two)}</td>
                <td className={classes.colStatus}>
                  {r.differenceMt === null ? '—' : <IssueBadge issue={r.issue} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableBox>
    </Panel>
  );
}

function LegsPanel({ analysis }: { analysis: FuelAnalysis }) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string) => t(`voyage.fuelAnalysis.${key}`);
  const c = (key: string) => k(`columns.${key}`);
  const title = k('legs');
  return (
    <Panel title={title} icon={<IconRoute size={20} stroke={1.8} />} flush>
      {analysis.legs.length === 0 ? (
        <p className={record.empty}>{k('noLegs')}</p>
      ) : (
        <TableBox label={title}>
          <table className={`${table.table} ${record.table} ${classes.wrapHead}`}>
            <thead>
              <tr>
                <th scope="col" className={record.colNo}>
                  {c('leg')}
                </th>
                <th scope="col">{c('from')}</th>
                <th scope="col">{c('to')}</th>
                <th scope="col" className={record.num}>
                  {c('distance')}
                </th>
                <th scope="col" className={record.num}>
                  {c('duration')}
                </th>
                <th scope="col" className={record.num}>
                  {c('consumption')}
                </th>
                <th scope="col" className={record.num}>
                  {c('daily')}
                </th>
                <th scope="col" className={record.num}>
                  {c('kgPerNm')}
                </th>
              </tr>
            </thead>
            <tbody>
              {analysis.legs.map((leg) => (
                <tr key={leg.no}>
                  <td className={record.colNo}>{leg.no}</td>
                  <td className={record.ellipsis} title={leg.from ?? undefined}>
                    {leg.from ?? '—'}
                  </td>
                  <td className={record.ellipsis} title={leg.to ?? undefined}>
                    {leg.to ?? '—'}
                  </td>
                  <td className={record.num}>{n(leg.distanceNm, 0)}</td>
                  <td className={record.num}>{formatDuration(leg.hours)}</td>
                  <td
                    className={`${record.num} ${record.strong}`}
                    title={Object.entries(leg.fuels)
                      .map(([fuel, mt]) => `${fuel} ${n(mt, two)}`)
                      .join(' · ')}
                  >
                    {n(leg.totalMt, two)}
                  </td>
                  <td className={record.num}>{n(leg.dailyMt, two)}</td>
                  <td className={record.num}>{n(leg.kgPerNm, one)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
      )}
    </Panel>
  );
}

function PeriodsPanel({ analysis }: { analysis: FuelAnalysis }) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string) => t(`voyage.fuelAnalysis.${key}`);
  const c = (key: string) => k(`columns.${key}`);
  const title = k('periods');
  return (
    <Panel title={title} icon={<IconCalendarStats size={20} stroke={1.8} />} flush>
      {analysis.periods.length === 0 ? (
        <p className={record.empty}>{k('noPeriods')}</p>
      ) : (
        <TableBox label={title}>
          <table className={`${table.table} ${record.table} ${classes.wrapHead}`}>
            <thead>
              <tr>
                <th scope="col">{c('period')}</th>
                <th scope="col">{c('place')}</th>
                <th scope="col" className={record.colDate}>
                  {c('start')}
                </th>
                <th scope="col" className={record.num}>
                  {c('duration')}
                </th>
                <th scope="col" className={record.num}>
                  {c('consumption')}
                </th>
                <th scope="col" className={record.num}>
                  {c('daily')}
                </th>
              </tr>
            </thead>
            <tbody>
              {analysis.periods.map((period) => (
                <tr key={period.start}>
                  <td>
                    <span className={record.state} data-state={period.state}>
                      {k(`states.${period.state}`)}
                    </span>
                  </td>
                  <td className={record.ellipsis} title={period.place ?? undefined}>
                    {period.place ?? '—'}
                  </td>
                  <td>{formatDateTime(period.start, 0)}</td>
                  <td className={record.num}>{formatDuration(period.hours)}</td>
                  <td
                    className={`${record.num} ${record.strong}`}
                    title={Object.entries(period.fuels)
                      .map(([fuel, mt]) => `${fuel} ${n(mt, two)}`)
                      .join(' · ')}
                  >
                    {n(period.totalMt, two)}
                  </td>
                  <td className={record.num}>{n(period.dailyMt, two)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
      )}
    </Panel>
  );
}

function RobFlowPanel({ analysis }: { analysis: FuelAnalysis }) {
  const { t } = useTranslation();
  const n = useNumber();
  const labels = useReportLabels();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.fuelAnalysis.${key}`, options);
  const c = (key: string) => k(`columns.${key}`);
  const fuels = [...new Set(analysis.robFlow.map((row) => row.fuel))];
  const [picked, setPicked] = useState<string | null>(null);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const fuel = picked && fuels.includes(picked) ? picked : (fuels[0] ?? null);
  const rows = analysis.robFlow.filter((row) => row.fuel === fuel);
  const shown = onlyIssues ? rows.filter((row) => row.issue !== null) : rows;
  const title = k('robFlow');
  return (
    <Panel
      title={title}
      icon={<IconListDetails size={20} stroke={1.8} />}
      flush
      extra={
        analysis.issues > 0 ? (
          <StatusBadge tone="warning" withIcon>
            {k('issuesFound', { count: analysis.issues })}
          </StatusBadge>
        ) : null
      }
    >
      {fuel === null ? (
        <p className={record.empty}>{k('noFlow')}</p>
      ) : (
        <>
          <div className={classes.flowTools}>
            <Select
              label={k('robFuel')}
              data={fuels}
              value={fuel}
              allowDeselect={false}
              onChange={setPicked}
              className={classes.fuelSelect}
            />
            <Checkbox
              label={k('onlyIssues')}
              checked={onlyIssues}
              onChange={(event) => setOnlyIssues(event.currentTarget.checked)}
            />
          </div>
          <RobTrend rows={rows} fuel={fuel} />
          {shown.length === 0 ? (
            <p className={record.empty}>{k('noIssues')}</p>
          ) : (
            <TableBox label={`${title} · ${fuel}`}>
              <table
                className={`${table.table} ${record.table} ${classes.wrapHead} ${classes.flowTable}`}
              >
                <thead>
                  <tr>
                    <th scope="col" className={record.colDate}>
                      {c('date')}
                    </th>
                    <th scope="col" className={classes.colReport}>
                      {c('report')}
                    </th>
                    <th scope="col" className={record.num}>
                      {c('previous')}
                    </th>
                    <th scope="col" className={record.num}>
                      {c('consumed')}
                    </th>
                    <th scope="col" className={record.num}>
                      {c('bunkered')}
                    </th>
                    <th scope="col" className={record.num}>
                      {c('expected')}
                    </th>
                    <th scope="col" className={record.num}>
                      {c('rob')}
                    </th>
                    <th scope="col" className={record.num}>
                      {c('difference')}
                    </th>
                    <th scope="col" className={classes.colStatus}>
                      {c('status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => (
                    <tr
                      key={`${row.reportedAt}-${row.reportId ?? row.kind}`}
                      data-issue={row.issue ?? undefined}
                    >
                      <td>{formatDateTime(row.reportedAt, 0)}</td>
                      <td className={record.ellipsis} title={labels.typeLabel(row.kind)}>
                        {labels.typeLabel(row.kind)}
                      </td>
                      <td className={record.num}>{n(row.previousMt, two)}</td>
                      <td className={record.num}>{n(row.consumptionMt, two)}</td>
                      <td className={record.num}>
                        {row.bunkeredMt > 0 ? n(row.bunkeredMt, two) : '—'}
                      </td>
                      <td className={record.num}>{n(row.expectedMt, two)}</td>
                      <td className={`${record.num} ${record.strong}`}>{n(row.robMt, two)}</td>
                      <td className={record.num}>{n(row.differenceMt, two)}</td>
                      <td className={classes.colStatus}>
                        <IssueBadge issue={row.issue} opening={row.opening} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableBox>
          )}
        </>
      )}
    </Panel>
  );
}

const TREND_W = 720;
const TREND_H = 160;
const TREND_PAD = 10;

/**
 * Seçilen yakıtın ROB eğilimi (tek seri; başlık adını söyler): rapor rapor ROB, farklı olan
 * raporlar uyarı rengiyle ve simgeyle; noktanın ipucunda tarih, rapor ve ROB. Tablo aynı veriyi
 * metinle verir (erişilebilirlik).
 */
function RobTrend({ rows, fuel }: { rows: readonly RobFlowRow[]; fuel: string }) {
  const { t } = useTranslation();
  const n = useNumber();
  const labels = useReportLabels();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.fuelAnalysis.${key}`, options);
  const points = rows.filter((row): row is RobFlowRow & { robMt: number } => row.robMt !== null);
  if (points.length < 2) return null;
  const times = points.map((p) => Date.parse(p.reportedAt));
  const values = points.map((p) => p.robMt);
  const [t0, t1] = [Math.min(...times), Math.max(...times)];
  const [v0, v1] = [Math.min(...values), Math.max(...values)];
  const x = (time: number) =>
    TREND_PAD + (t1 > t0 ? ((time - t0) / (t1 - t0)) * (TREND_W - 2 * TREND_PAD) : 0);
  const y = (value: number) =>
    TREND_H - TREND_PAD - (v1 > v0 ? ((value - v0) / (v1 - v0)) * (TREND_H - 2 * TREND_PAD) : 0);
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(times[i] as number)},${y(p.robMt)}`)
    .join(' ');
  const title = k('trend', { fuel });
  return (
    <figure className={classes.trend}>
      <figcaption className={record.timeTitle}>{title}</figcaption>
      <div className={classes.trendBody}>
        <div className={classes.axis} aria-hidden>
          <span>{n(v1, 0)}</span>
          <span>{n(v0, 0)}</span>
        </div>
        <svg
          viewBox={`0 0 ${TREND_W} ${TREND_H}`}
          className={classes.trendSvg}
          role="img"
          aria-label={title}
        >
          <line
            x1={TREND_PAD}
            x2={TREND_W - TREND_PAD}
            y1={TREND_H - TREND_PAD}
            y2={TREND_H - TREND_PAD}
            className={classes.grid}
          />
          <line
            x1={TREND_PAD}
            x2={TREND_W - TREND_PAD}
            y1={TREND_PAD}
            y2={TREND_PAD}
            className={classes.grid}
          />
          <path d={path} className={classes.line} />
          {points.map((p, i) => (
            <g key={`${p.reportedAt}-${p.reportId ?? i}`}>
              <circle
                cx={x(times[i] as number)}
                cy={y(p.robMt)}
                r={p.issue ? 5 : 4}
                className={classes.point}
                data-issue={p.issue ?? undefined}
              />
              <circle cx={x(times[i] as number)} cy={y(p.robMt)} r={12} className={classes.hit}>
                <title>
                  {k('trendPoint', {
                    date: formatDateTime(p.reportedAt, 0),
                    report: labels.typeLabel(p.kind),
                    rob: n(p.robMt, two),
                  })}
                  {p.issue ? ` · ${k(`status.${p.issue}`)} ${n(p.differenceMt, two)} MT` : ''}
                </title>
              </circle>
            </g>
          ))}
        </svg>
      </div>
      <div className={classes.axisX} aria-hidden>
        <span>{formatDateTime(new Date(t0).toISOString(), 0)}</span>
        {points.some((p) => p.issue) ? (
          <span className={classes.trendKey}>
            <IconAlertTriangle size={14} /> {k('tiles.warnings')}
          </span>
        ) : null}
        <span>{formatDateTime(new Date(t1).toISOString(), 0)}</span>
      </div>
    </figure>
  );
}
